import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/admin/ResourceTable";
import TimeTabs from "@/admin/TimeTabs";
import { ScanLine, Users, IndianRupee, Ticket, Megaphone, Store, Package, Wallet, Loader2, ArrowUp, ArrowDown, Minus, UserPlus, UserCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

function Delta({ m }) {
  if (!m || m.dir === "flat") return <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-gray-400"><Minus className="h-3 w-3" /> 0</span>;
  const up = m.dir === "up";
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${up ? "text-green-600" : "text-red-600"}`} data-testid="kpi-delta">
      <Icon className="h-3.5 w-3.5" /> {Math.abs(m.delta).toLocaleString("en-IN")}
      <span className="text-gray-400 font-normal ml-0.5">vs prev</span>
    </span>
  );
}

function KpiCard({ icon: Icon, label, metric, value, sub, testid }) {
  const val = metric ? metric.value : value;
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</span>
        <div className="h-8 w-8 rounded-lg bg-[#FDF2F2] flex items-center justify-center text-[#8B0000]"><Icon className="h-4 w-4" /></div>
      </div>
      <div className="mt-3 kpi-num text-3xl text-gray-900">{(val ?? 0).toLocaleString("en-IN")}</div>
      <div className="mt-1.5 flex items-center justify-between">
        {sub ? <span className="text-xs text-gray-500">{sub}</span> : <span />}
        {metric && <Delta m={metric} />}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [tp, setTp] = useState({ period: "today", frm: "", to: "" });

  useEffect(() => {
    const params = { period: tp.period };
    if (tp.period === "custom") { if (!tp.frm || !tp.to) return; params.frm = tp.frm; params.to = tp.to; }
    setD(null);
    api.get("/dashboard/loyalty", { params }).then((r) => setD(r.data));
  }, [tp]);

  const t = d?.totals;
  const m = d?.metrics;
  const cur = t?.currency || "₹";

  return (
    <div>
      <PageHeader title="Loyalty Dashboard" subtitle="Live metrics with period-over-period comparison">
        <TimeTabs value={tp} onChange={setTp} />
      </PageHeader>

      {!d ? <div className="py-20 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#8B0000] mx-auto" /></div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={ScanLine} label="Successful Scans" metric={m.successful_scans} sub={`${m.invalid_scans.value} invalid`} testid="kpi-scans" />
            <KpiCard icon={Package} label="Unique Products (SKU)" metric={m.unique_products} sub="scanned in period" testid="kpi-unique-products" />
            <KpiCard icon={IndianRupee} label="UPI Disbursed" value={`${cur}${(m.upi_disbursed.value).toLocaleString("en-IN")}`} sub={<Delta m={m.upi_disbursed} />} testid="kpi-upi" />
            <KpiCard icon={Wallet} label="Points Issued" metric={m.points_issued} sub="in period" testid="kpi-points" />
          </div>

          {/* Active painters split */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm lg:col-span-1" data-testid="kpi-active-painters">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Active Painters</span>
                <div className="h-8 w-8 rounded-lg bg-[#FDF2F2] flex items-center justify-center text-[#8B0000]"><Users className="h-4 w-4" /></div>
              </div>
              <div className="mt-3 kpi-num text-3xl text-gray-900">{(m.active_painters.value).toLocaleString("en-IN")}</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="bg-green-50 rounded-lg p-2.5" data-testid="painters-new">
                  <div className="flex items-center gap-1 text-green-700"><UserPlus className="h-3.5 w-3.5" /><span className="text-[11px] font-semibold uppercase">New</span></div>
                  <div className="kpi-num text-xl text-gray-900">{(m.active_painters.new).toLocaleString("en-IN")}</div>
                  <div className="text-[10px] text-gray-500">never scanned before</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2.5" data-testid="painters-existing">
                  <div className="flex items-center gap-1 text-blue-700"><UserCheck className="h-3.5 w-3.5" /><span className="text-[11px] font-semibold uppercase">Existing</span></div>
                  <div className="kpi-num text-xl text-gray-900">{(m.active_painters.existing).toLocaleString("en-IN")}</div>
                  <div className="text-[10px] text-gray-500">returning</div>
                </div>
              </div>
            </div>

            {/* network totals */}
            <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <KpiCard icon={Ticket} label="Redemption" value={`${t.redemption_rate}%`} sub={`${t.coupons_used}/${t.coupons_total}`} testid="kpi-redemption" />
              <KpiCard icon={Store} label="Dealers" value={t.total_dealers} sub="network" testid="kpi-dealers" />
              <KpiCard icon={Users} label="Users" value={t.total_painters} sub="painters + contractors" testid="kpi-painters" />
              <KpiCard icon={Megaphone} label="Campaigns" value={t.active_campaigns} sub="active" testid="kpi-campaigns" />
            </div>
          </div>

          <div className="mt-6 bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="font-display font-bold text-gray-900 mb-4">Top Dealers by Scans (all-time)</h3>
            {d.top_dealers.length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">No scan activity yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={d.top_dealers} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="dealer" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: "#FDF2F2" }} />
                  <Bar dataKey="scans" radius={[0, 6, 6, 0]}>{d.top_dealers.map((_, i) => <Cell key={i} fill="#8B0000" />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
